import Highcharts from "./point.extensions";
import HighchartsReact from "highcharts-react-official";
import React, {useState, useEffect, useRef} from "react";
import ProjectService from "./ProjectService";
import HighchartsBoost from 'highcharts/modules/boost';
import { useTranslation } from "react-i18next";
import Spinner from "../loader/Spinner";
import moment from "moment-timezone";
import { Tooltip } from 'react-tooltip'


type SlowTraceProps = {
    did: string;
    start_time: any;
    end_time: any;
};
const SlowTrace = (props: SlowTraceProps) => {
    const { t } = useTranslation();
    HighchartsBoost(Highcharts);
    const chartRef = useRef<any>();
    const chart1Ref = useRef<any>();
    const chart2Ref = useRef<any>();
    const chart3Ref = useRef<any>();
    const [xyzData, setXYZData] = useState([]);
    const [xData, setXData] = useState([]);
    const [yData, setYData] = useState([]);
    const [zData, setZData] = useState([]);
    const [componentRendered, setComponentRendered] = useState<number>(0);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [timezone, setTimezone] = useState<any>();
    const [csvFileName, setCSVFileName] = useState<string>('');
    const [isLogarithmic, setIsLogarithmic] = useState<boolean>(false);
    const [isIndividualGraph, setIsIndividualGraph] = useState<boolean>(true);
    const [maxX, setMaxX] = useState<string>();
    const [maxY, setMaxY] = useState<string>();
    const [maxZ, setMaxZ] = useState<string>();

    const graphHeight = (window.innerHeight - 50) / 3;
    const projectService = new ProjectService();
    let lastDay: any = null;

    const graphTitle = `${t('slow_trace_graph')} - ${props.did[0].length > 0 && props.did[0].split('_')[0]}`

    const yourThreshold = 1 * 60 * 1000;
    const gaps = findDataGaps(xyzData);
    function findDataGaps(data : any) {
        const gaps: any = [];
        if (data.length <= 1) {
            return gaps; // No gaps in a single data point or empty data.
        }

        for (let i = 1; i < data.length; i++) {
            const previousTimestamp = data[i - 1].x;
            const currentTimestamp = data[i].x;


            const timeDifference = currentTimestamp - previousTimestamp;

            if (timeDifference > yourThreshold) {
                // Identify a gap, record the start and end timestamps of the gap
                const gapStart = previousTimestamp + 1;
                const gapEnd = currentTimestamp - 1;

                gaps.push({ start: gapStart, end: gapEnd });
            }
        }

        return gaps;
    }

    const slow_trace_graph = {
        credits: {
            enabled: false,
        },
        boost: {
            useGPUTranslations: true,
            usePreAllocated: true,
        },
        plotOptions: {
            series: {
                boostThreshold: 2000, // Set the threshold for boosting
            },
        },
        chart: {
            type: "scatter",
            zoomType: "x",
            height: 400,
        },
        xAxis: {
            crosshair: true,
            title: {
                text: `${t('relative_timein')} [HH:MM:SS:MS]`,
            },
            type: "datetime",
            label: {
                format: "%H:%M:%S.%L",
            },
            labels: {
                formatter: function(this: any) {
                    const currentDay: string = moment.tz(this.value, timezone).format("DD.MMM");
                    const time: string = moment.tz(this.value, timezone).format("HH:mm:ss");
                    if (lastDay !== currentDay) {
                        lastDay = currentDay;
                        return currentDay;
                    } else {
                        return time;
                    }
                }
            },
            events: {
                afterSetExtremes: function (event: any) {
                    let startTime;
                    let endTime
                    if( chartRef?.current?.chart.xAxis[0]['userMax'] && chartRef?.current?.chart.xAxis[0]['userMin'] !== undefined){
                         startTime =  Math.round(event.min / 1000);
                         endTime = Math.round(event.max / 1000);
                    }
                    else{
                        startTime =  props.start_time;
                        endTime = props.end_time;
                    }
                    fetchAPI(props.did, startTime, endTime)
                },
            },
        },
        yAxis: {
            type: isLogarithmic ? 'logarithmic' : 'linear',
            title: {
                text: "mm/s",
            },
        },
        title: {
            text: graphTitle,
        },
        subtitle: {
            text: "",
        },
        tooltip: {
            formatter: function(this: Highcharts.TooltipFormatterContextObject) {
                const dateStr: string = moment.tz(this.x, timezone).format("DD.MM.YYYY HH:mm:ss");
                const formattedValue: string = (this.y ?? 0).toFixed(3).toString();
                return `<b>${this.series.name}</b><br>Date: ${dateStr}<br>Value: ${formattedValue} mm/s`;
            },
        },
        series: [
            {
                turboThreshold: xyzData.length,
                data: xyzData.map((point: any) => [point.x, point.y]),
                showInLegend: true,
                name: "X-Axis",
                marker: {
                    radius: 2,
                },
            },
            {
                turboThreshold: xyzData.length,
                data: xyzData.map((point: any) => [point.x, point.z]),
                showInLegend: true,
                name: "Y-Axis",
                marker: {
                    radius: 2,
                },
            },
            {
                turboThreshold: xyzData.length,
                data: xyzData.map((point: any) => [point.x, point.w]),
                showInLegend: true,
                name: "Z-Axis",
                marker: {
                    radius: 2,
                },
            },
        ],
    };

    const generatePlotBandsForZoom = (data: any) => {
        const plotBands: any = [];
        let prevDataPoint: any = null;
        const minDataGapMillis = 3600000;

        data.forEach((point: any) => {
            if (prevDataPoint !== null) {
                const timeDifference = point.x - prevDataPoint.x;
                if (timeDifference > minDataGapMillis) {
                    plotBands.push({
                        color: "rgba(255, 0, 0, 0.2)",
                        from: prevDataPoint.x,
                        to: point.x,
                    });
                }
            }
            prevDataPoint = point;
        });

        return plotBands;
    };

    const sharedXAxis = (isShown : boolean) => {
        return {
            type: "datetime",
            crosshair: true,
            labels: {
                enabled: isShown,
                formatter: function (this: any) {
                    if (isShown) {
                        const currentDay: string = moment
                            .tz(this.value, timezone)
                            .format("DD.MMM");
                        const time: string = moment
                            .tz(this.value, timezone)
                            .format("HH:mm:ss");
                        if (lastDay !== currentDay) {
                            lastDay = currentDay;
                            return currentDay;
                        } else {
                            return time;
                        }
                    } else {
                        // Return a different format or an empty string if labels are not shown
                        return "";
                    }
                },
                // format: "{value:%d.%m.%Y %H:%M:%S}",
            },
            events: {
                afterSetExtremes: function (event: any) {
                    chart1Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
                    chart2Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
                    chart3Ref?.current?.chart.xAxis[0].setExtremes(event.min, event.max);
                },
            },
        };
    };
    const sharedPlotOption = () => {
        return {
            series: {
                boostThreshold: 2000,
                cropThreshold: xyzData.length,
            },
        };
    };
    const formatTooltipDate = (timestamp: any, timezone: string) => {
        return moment.tz(timestamp, timezone).format("DD.MM.YYYY HH:mm:ss");
    };

    const slowTraceX = {
        chart: {
            type: "column",
            zoomType: "x",
            height: graphHeight,
        },
        credits: {
            enabled: false,
        },
        boost: {
            useGPUTranslations: true,
            usePreAllocated: true,
        },
        plotOptions: sharedPlotOption(),
        xAxis: sharedXAxis(false),
        yAxis: {
            type: isLogarithmic ? 'logarithmic' : 'linear',
            title: {
                text: "X-Axis",
            },
        },
        title: {
            text: graphTitle,
        },
        subtitle: {
            text: "",
        },
        tooltip: {
            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                const dateStr: string = formatTooltipDate(this.x, timezone);
                return `<b>${this.series?.name}</b><br>Date: ${dateStr}<br>Value: ${this.y}`;
            }
        },
        series: [
            {
                turboThreshold: xyzData.length,
                data: xyzData.map((point: any) => [point.x, point.y]),
                showInLegend: false,
                name: "X-Axis",
            },
        ],
    };
    const slowTraceY = {
        chart: {
            type: "column",
            zoomType: "x",
            height: graphHeight,
        },
        credits: {
            enabled: false,
        },
        boost: {
            useGPUTranslations: true,
            usePreAllocated: true,
        },
        plotOptions: sharedPlotOption(),
        xAxis: sharedXAxis(false),
        yAxis: {
            type: isLogarithmic ? 'logarithmic' : 'linear',
            title: {
                text: "Y-Axis",
            },
        },
        title: {
            text: "",
        },
        subtitle: {
            text: "",
        },
        tooltip: {
            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                const dateStr: string = formatTooltipDate(this.x, timezone);
                return `<b>${this.series?.name}</b><br>Date: ${dateStr}<br>Value: ${this.y}`;
            }
        },
        series: [
            {
                turboThreshold: xyzData.length,
                data: xyzData.map((point: any) => [point.x, point.z]),
                showInLegend: false,
                name: "Y-Axis",
            },
        ],
    };
    const slowTraceZ = {
        chart: {
            type: "column",
            zoomType: "x",
            height: graphHeight,
        },
        credits: {
            enabled: false,
        },
        boost: {
            useGPUTranslations: true,
            usePreAllocated: true,
        },
        plotOptions: sharedPlotOption(),
        xAxis: sharedXAxis(true),
        yAxis: {
            type: isLogarithmic ? 'logarithmic' : 'linear',
            title: {
                text: "Z-Axis",
            },
        },
        title: {
            text: "",
        },
        subtitle: {
            text: "",
        },
        tooltip: {
            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                const dateStr: string = formatTooltipDate(this.x, timezone);
                return `<b>${this.series?.name}</b><br>Date: ${dateStr}<br>Value: ${this.y}`;
            }
        },
        series: [
            {
                turboThreshold: xyzData.length,
                data: xyzData.map((point: any) => [point.x, point.w]),
                showInLegend: false,
                name: "Z-Axis",
            },
        ],
    };

    const fetchAPI = async (did: string, start_time: any, end_time: any) => {

        projectService
            .getSlowTrace(did, start_time, end_time)
            .then((response) => {
                if (response['channelX'].length > 0) {
                    const date = new Date(+response['channelX'][0]['start_time'] * 1000); // Convert to milliseconds
                    const year = date.getFullYear();
                    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-based
                    const day = date.getDate().toString().padStart(2, '0');
                    const hours = date.getHours().toString().padStart(2, '0');
                    const minutes = date.getMinutes().toString().padStart(2, '0');
                    const seconds = date.getSeconds().toString().padStart(2, '0');
                    const formattedDate = `${year}${month}${day}_${hours}${minutes}${seconds}`;
                    const tempCsvFile = `${response['channelX'][0]['station_name']}_${formattedDate}.csv`;
                    setCSVFileName(tempCsvFile)
                }
                const max_X = localStorage.getItem('lang') === 'en' ? Number(response.max_X).toFixed(3) : Number(response.max_X).toFixed(3).toString().replace(/\./g, ',')
                setMaxX(max_X);
                const max_Y = localStorage.getItem('lang') === 'en' ? Number(response.max_Y).toFixed(3) : Number(response.max_Y).toFixed(3).toString().replace(/\./g, ',')
                setMaxY(max_Y);
                const max_Z = localStorage.getItem('lang') === 'en' ? Number(response.max_Z).toFixed(3) : Number(response.max_Z).toFixed(3).toString().replace(/\./g, ',')
                setMaxZ(max_Z);
                setXYZData(
                    response.channelX.map((pointX: any, index: number) => ({
                        x: +moment.tz(pointX.start_time * 1000, timezone).valueOf(),
                        y: +pointX.slow_trace.split(',')[1].slice(0, -1),
                        z: +response.channelY[index].slow_trace.split(',')[1].slice(0, -1),
                        w: +response.channelZ[index].slow_trace.split(',')[1].slice(0, -1),
                        date: moment
                            .tz(pointX.start_time * 1000, timezone)
                            .format("DD.MM.YYYY HH:mm:ss"),
                    }))
                );
            });
        setIsLoading(false);
    }

    useEffect(()=>{
        const timeZone : any= localStorage.getItem('timezone');
        projectService.getSlowTrace(props.did, props.start_time, props.end_time).then(data => {
            const formattedXData = data.channelX.map((pointX : any, index: number) => ({
                x: moment.tz(pointX.start_time * 1000, timeZone).valueOf(),
                y: +pointX.slow_trace.split(',')[1].slice(0, -1),
                date: moment
                    .tz(pointX.start_time * 1000, timeZone)
                    .format("YYYY-MM-DD HH:mm:ss"),
            }));

            const formattedYData = data.channelY.map((pointY : any, index: number) => ({
                x: moment.tz(pointY.start_time * 1000, timeZone).valueOf(),
                y: +pointY.slow_trace.split(',')[1].slice(0, -1),
                date: moment
                    .tz(pointY.start_time * 1000, timeZone)
                    .format("YYYY-MM-DD HH:mm:ss"),
            }));

            const formattedZData = data.channelZ.map((pointZ : any, index: number) => ({
                x: moment.tz(pointZ.start_time * 1000, timeZone).valueOf(),
                y: +pointZ.slow_trace.split(',')[1].slice(0, -1),
                date: moment
                    .tz(pointZ.start_time * 1000, timeZone)
                    .format("YYYY-MM-DD HH:mm:ss"),
            }));

            if (formattedXData.length === 0) {
                formattedXData.push({
                    x: moment().valueOf(),
                    y: null,
                });
            }

            if (formattedYData.length === 0) {
                formattedYData.push({
                    x: moment().valueOf(),
                    y: null,
                });
            }

            if (formattedZData.length === 0) {
                formattedZData.push({
                    x: moment().valueOf(),
                    y: null,
                });
            }

            setXData(formattedXData);
            setYData(formattedYData);
            setZData(formattedZData);
        })
    }, [props.did, props.start_time, props.end_time])

    useEffect( () => {
        setIsLoading(true);
        setTimezone(localStorage.getItem('timezone'));
        fetchAPI(props.did, props.start_time, props.end_time)
    }, [props.did, props.start_time, props.end_time]);

    useEffect(() => {
        ["mousemove", "touchmove", "touchstart"].forEach(function (eventType) {
            document
                ?.getElementById("hightchart-wrapper")
                ?.addEventListener(eventType, function (e: any) {
                    let chart, point, i, event: any;

                    for (i = 0; i < Highcharts.charts.length; i = i + 1) {
                        chart = Highcharts.charts[i];
                        if (chart) {
                            // Find coordinates within the chart
                            event = chart.pointer.normalize(e);
                            // Get the hovered point
                            chart.series.forEach((series: any) => {
                                const point = series.searchPoint(event, true);
                                if (point) {
                                    try {
                                        point.highlight(e);
                                    } catch (err) {
                                        // pass;
                                    }
                                }
                            });
                        }
                    }
                });
        });
        return;
    }, [componentRendered, props.did, props.start_time, props.end_time]);
    return (
        <div>
            {!isLoading ?
                <div>
                    <div className={'grid align-items-center justify-content-between mt-3'}>
                        <div className={'lg:col-11'}>
                           <div className={'grid'}>
                               <div className={'lg:col-12'}>
                                   <div className={'p-card px-2 py-4 mb-3 flex flex-wrap'}>
                                       <div className={'flex mx-2'}>
                                           <div style={{width:'120px', fontWeight: 'bold'}}>Max_X:</div>
                                           <div>{maxX} [mm/s]</div>
                                       </div>
                                       <div className={'flex mx-6'}>
                                           <div style={{width:'120px', fontWeight: 'bold'}}>Max_Y:</div>
                                           <div>{maxY} [mm/s]</div>
                                       </div>
                                       <div className={'flex mx-6'}>
                                           <div style={{width:'120px', fontWeight: 'bold'}}>Max_Z:</div>
                                           <div>{maxZ} [mm/s]</div>
                                       </div>
                                   </div>
                               </div>
                           </div>
                        </div>
                        <div className={'lg:col-1'}>
                            <div className='flex flex-wrap align-items-center'>
                                <div className="ml-auto mb-3">
                                    <div className="grid">
                                        <Tooltip id="change-graph"/>
                                        <img
                                            alt={t("graph_change")}
                                            src={isIndividualGraph ? "/images/chart-bar.svg" : "/images/chart-line.svg"}
                                            className= "p-button-raised p-button-text csv-img change-graph"
                                            onClick={()=>{xyzData.length > 0 && setIsIndividualGraph(!isIndividualGraph);  setComponentRendered(componentRendered + 1);}}
                                            style={{cursor: xyzData.length > 0 ? 'pointer' : 'no-drop' }}
                                            data-tooltip-id={"change-graph"}
                                            data-tooltip-content={isIndividualGraph ? t('change_bar_graph') : t('change_slow_trace') }
                                        />
                                        <Tooltip id="change-yaxis"/>

                                        {!isLogarithmic &&
                                            <p className="m-1 graph-type" onClick={()=>{setIsLogarithmic(!isLogarithmic)}}
                                               data-tooltip-id={"change-yaxis"}
                                               data-tooltip-content={t('logarithmic')}>log
                                                <sub>n</sub>
                                            </p>}
                                        {isLogarithmic &&
                                            <p className="m-1 graph-type" onClick={()=>{setIsLogarithmic(!isLogarithmic)}}
                                               data-tooltip-id={"change-yaxis"}
                                               data-tooltip-content={t('linear')}>lin
                                            </p>}
                                    </div>


                                    {/*<img*/}
                                    {/*    alt={t("logarithmic")}*/}
                                    {/*    src={isLogarithmic ? "/images/linear.png" : "/images/log.png"}*/}
                                    {/*    className= "p-button-raised p-button-text csv-img ml-2 change-yaxis"*/}
                                    {/*    onClick={()=>{setIsLogarithmic(!isLogarithmic)}}*/}
                                    {/*    data-tooltip-id={"change-yaxis"}*/}
                                    {/*    data-tooltip-content={isLogarithmic ? t('linear') : t('logarithmic') }*/}
                                    {/*/>*/}
                                   
                                </div>
                            </div>
                        </div>
                    </div>

                    {isIndividualGraph ?  <HighchartsReact
                        highcharts={Highcharts}
                        ref={chartRef}
                        options={slow_trace_graph}
                    />:
                       <div id="hightchart-wrapper">
                            <HighchartsReact
                                highcharts={Highcharts}
                                ref={chart1Ref}
                                options={slowTraceX}
                            />
                            <HighchartsReact
                                highcharts={Highcharts}
                                ref={chart2Ref}
                                options={slowTraceY}
                            />
                            <HighchartsReact
                                highcharts={Highcharts}
                                ref={chart3Ref}
                                options={slowTraceZ}
                            />
                        </div>}

            </div>: <div style={{height: '150px', textAlign: 'center'}}> <Spinner /></div>}


        </div>
    );
};

export default SlowTrace;
